import React, { useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';
import SectionContainer from '../../containers/PageBuilder/SectionBuilder/SectionContainer';
import { NamedLink } from '../../components';

import { getSeasonMessage, fensterLabel } from '../seasons';
import { gewaehltesFenster, periodenFuer } from '../clubFields';
import { getCategoryImage } from '../categoryImages';
import {
  getOfferAllowance,
  getPlan,
  canSubmitOffer,
  getReleaseTime,
  isReleasedForDealer,
  formatWaitingTime,
  getVisibilityDelayHours,
} from '../membership';
import { getDeadlineStatus } from '../deadlines';
import { sortByUrgency, getDealerCategories, getCategoryLabel } from '../matching';
import { OFFER_FIELDS, getOfferFieldValue, getOfferFieldRaw } from '../offerFields';
import { getReorderSuggestions } from '../reorder';
import {
  findBundlePartners,
  summarizeBundle,
  getAuthorId,
  groupBundles,
  getQuantity,
  poolGroups,
  isBundleOpen,
  isStillOpen,
  MAX_ANLAGEN,
  istBuendelfaehig,
  mehrdeutigeFelder,
  fehlendeFelder,
  stehtImPool,
} from '../bundles';
import { buildAwardHtml } from '../award';
import OfferPdfButton from '../OfferPdfButton/OfferPdfButton';
import SectionWelcome from '../SectionWelcome/SectionWelcome';
// Die Eckdaten einer Anfrage - derselbe Baustein wie in den Suchergebnissen.
import AnfrageChips, { useFeldInfo } from '../AnfrageChips/AnfrageChips';
import { createSlug } from '../../util/urlHelpers';
// FairwayBid: der Anstoss, wenn nach Tagen nichts kommt.
import { stilleAnfrage } from '../aussicht';
// FairwayBid: Vorstart - Registrierung offen, Portal noch zu.
import { istVorStart, tageBisStart, VORSTART_TEXTE, LAUNCH_DATE_LABEL } from '../launch';
import { istDoppelteUeberschrift } from '../audience';
import { describeCoverage, COUNTRY_LABELS } from '../coverage';
import { baueVorlage, merkeVorlage, MENGENFELD } from '../bundleDraft';
import { KATEGORIE_MIT_STAFFEL } from '../quantityField';
import { useConfiguration } from '../../context/configurationContext';
import { CATEGORY_LABELS } from '../matching';
import { dealChance } from '../dealChance';
import DealRing from '../HaendlerBox/DealRing';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import {
  groupOffersByListing,
  summarizeOffers,
  sortOffersForComparison,
  getOfferAmount,
  formatAmount,
  getOfferStatus,
  countOffersInMonth,
  STATUS_LABELS,
} from '../offers';
import css from './SectionDashboard.module.css';

/**
 * FairwayBid – Startseite nach der Anmeldung
 *
 * Ersetzt die Abschnitte mit der Anchor-ID club-* bzw. dealer-* durch eine
 * lebende Ansicht: oben die Beschaffungsperiode (tagesaktuell aus
 * src/fairwaybid/seasons.js), darunter die eigenen Anfragen.
 *
 * Titel und Beschreibung kommen weiter aus der Console – der Abschnitt bleibt
 * also redaktionell pflegbar, nur der dynamische Teil kommt aus dem Code.
 */

const STATE_LABELS = {
  published: 'Veröffentlicht',
  draft: 'Entwurf',
  pendingApproval: 'Wird geprüft',
  closed: 'Geschlossen',
};

const SeasonBanner = ({ audience, currentUser }) => {
  /*
    Golfanlagen beschaffen nicht alle zur selben Zeit. Wer im Konto angegeben
    hat, wann er einkauft, bekommt nur sein eigenes Fenster zu sehen - alles
    andere ist eine Erinnerung an etwas, das ihn nichts angeht. Ohne Angabe
    bleibt es bei beiden Fenstern (periodenFuer gibt dann null zurueck).
  */
  const perioden = audience === 'provider' ? null : periodenFuer(currentUser);
  const eigenesFenster = audience === 'provider' ? null : gewaehltesFenster(currentUser);
  const message = getSeasonMessage(audience, new Date(), perioden);
  const { status } = message;
  const laufend = status.status === 'laufend';
  const urgent = laufend && status.endingSoon;
  const fenster = fensterLabel(status);

  const kurz = d => {
    const datum = d instanceof Date ? d : d ? new Date(d) : null;
    if (!datum || Number.isNaN(datum.getTime())) return null;
    return datum.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
  };
  const prozent = laufend ? Math.round((status.progress || 0) * 100) : null;

  return (
    <section className={classNames(css.season, { [css.seasonUrgent]: urgent })}>
      {/*
        Zwei Spalten statt eines Turms: links die Uhr, rechts was zu tun ist.
        Vorher standen Augenmarke, Ueberschrift, Linie, Zahl, Balken, Absatz
        und Knopf untereinander - das war einen ganzen Bildschirm hoch fuer
        eine Information, die in einen Blick passt.
      */}
      <div className={css.seasonGrid}>
        <div className={css.seasonTime}>
          <span className={css.seasonEyebrow}>Beschaffungsperiode</span>

          {status.restLabel ? (
            <p className={css.seasonStat}>
              <span className={css.seasonValue}>{status.restLabel}</span>
              <span className={css.seasonLabel}>{laufend ? 'verbleiben' : 'bis zum Beginn'}</span>
            </p>
          ) : null}

          {/*
            Der Zeitstrahl traegt jetzt seine Enden selbst. Vorher stand der
            Zeitraum als Fussnote unter der Zahl und der Balken daneben zeigte
            eine Fuellung ohne Massstab - zwei halbe Auskuenfte statt einer
            ganzen. Der Punkt ist das Heute.
          */}
          {laufend ? (
            <div className={css.seasonTimeline}>
              <div
                className={css.seasonTrack}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={prozent}
                aria-label={`${prozent}% der Periode vergangen`}
              >
                <div className={css.seasonTrackFill} style={{ width: `${prozent}%` }} />
                <span className={css.seasonToday} style={{ left: `${prozent}%` }} />
              </div>
              <div className={css.seasonEnds}>
                <span>{kurz(status.start)}</span>
                <span>{kurz(status.end)}</span>
              </div>
            </div>
          ) : fenster ? (
            <span className={css.seasonNote}>{fenster}</span>
          ) : null}
        </div>

        <div className={css.seasonMain}>
          <h3 className={css.seasonHeadline}>{message.headline}</h3>
          <p className={css.seasonBody}>{message.body}</p>
          <a className={css.seasonCta} href={message.ctaTo}>
            {message.ctaLabel}
          </a>
        </div>
      </div>

      {/*
        Solange die Anlage ihren Zeitraum nicht angegeben hat, erinnern wir an
        beide Fenster - und fragen einmal freundlich nach. Danach steht dort,
        wonach wir uns richten, damit die Angabe nachvollziehbar bleibt.
      */}
      {audience === 'provider' ? null : eigenesFenster ? (
        <p className={css.seasonSource}>
          Wir richten uns nach Ihrer Angabe im Konto: {eigenesFenster.label}.{' '}
          <NamedLink name="ManageAccountPage">Ändern</NamedLink>
        </p>
      ) : (
        <p className={css.seasonSource}>
          Sie beschaffen zu festen Zeiten im Jahr? Tragen Sie Ihren Zeitraum einmal im Konto ein,
          dann erinnern wir Sie nur dann, wenn es für Sie zählt.{' '}
          <NamedLink name="ManageAccountPage">Zeitraum angeben</NamedLink>
        </p>
      )}
    </section>
  );
};

/**
 * Das Bild einer Anfrage - dasselbe wie in der Suche.
 *
 * Anfragen haben meist kein eigenes Foto; dann steht dort das feste Bild der
 * Kategorie, genau wie in der Ergebnisliste. Ohne Bild ist eine Zeile nur
 * Text, und eine Liste aus Text sieht ueberall gleich aus.
 */
const bildFuer = listing => {
  const varianten = listing?.images?.[0]?.attributes?.variants || {};
  const schluessel =
    Object.keys(varianten).find(k => k.startsWith('listing-card')) || Object.keys(varianten)[0];
  return varianten[schluessel]?.url || getCategoryImage(listing);
};

/**
 * Die Mengen-Auswahl im Pool.
 *
 * Die Optionen kommen aus der Feldkonfiguration - derselben, aus der auch das
 * Anfrageformular seine Auswahl baut. Sie hier noch einmal hinzuschreiben
 * hiesse, zwei Listen zu pflegen, die sich beim ersten Preisschritt
 * unterscheiden.
 *
 * Gibt es das Feld in dieser Kategorie nicht, gibt es auch keine Auswahl -
 * dann ist die Menge kein Hebel und der Pool zeigt einfach die Anlagen.
 */
const useMengenOptionen = () => {
  const config = useConfiguration();
  const feld = (config?.listing?.listingFields || []).find(f => f?.key === MENGENFELD);
  return feld?.enumOptions || [];
};

/**
 * Beschriftungen der Merkmale, die eine Gruppe von der naechsten trennen.
 *
 * Seit Ausfuehrung und Farbe bei Range-Baellen gleich sein muessen, kann es
 * drei Kacheln "Range Golfbaelle" nebeneinander geben. Ohne die Merkmale
 * darunter waeren sie nicht auseinanderzuhalten.
 *
 * Die Texte kommen aus der Feldkonfiguration, nicht aus einer Liste hier -
 * sonst heisst dieselbe Farbe an zwei Stellen verschieden.
 */
const useMerkmalText = () => {
  const config = useConfiguration();
  const felder = config?.listing?.listingFields || [];

  return merkmale => {
    const teile = Object.entries(merkmale || {})
      .map(([key, wert]) => {
        if (!wert) return null;
        const optionen = felder.find(f => f?.key === key)?.enumOptions || [];
        // Mehrfachauswahlen sind zum Vergleich mit "+" zusammengezogen.
        return String(wert)
          .split('+')
          .map(w => optionen.find(o => o.option === w)?.label || w)
          .join(', ');
      })
      .filter(Boolean);

    return teile.length > 0 ? teile.join(' · ') : null;
  };
};

/**
 * Eine Kachel des Pools.
 *
 * Interaktiv an genau einer Stelle: Wer seine eigene Menge einstellt, sieht
 * sofort, was zusammenkaeme. Das ist das Argument fuer ein Buendel - eine
 * Zahl, die man selbst hat wachsen sehen, ueberzeugt anders als eine, die
 * dasteht.
 *
 * Die eingestellte Menge wandert mit in die Vorlage: Wer sie hier schon
 * gewaehlt hat, soll sie im Formular nicht noch einmal eintragen.
 */
const PoolGroup = ({ gruppe, mengenOptionen, merkmalText }) => {
  const [menge, setMenge] = useState('');
  const [offen, setOffen] = useState(false);

  const label = CATEGORY_LABELS[gruppe.category] || gruppe.category;
  const merkmale = merkmalText(gruppe.merkmale);

  // Drei Zustaende, und sie unterscheiden sich nicht in der Optik, sondern in
  // dem, was moeglich ist:
  //
  //   nurEigene  Ihre Anfrage steht im Pool, sonst niemand. Statusmeldung.
  //   istDabei   Sie sind Teil dieses Buendels. Nichts mehr anzuhaengen.
  //   sonst      fremde Anfragen, an die man sich haengen kann.
  const anlagen = gruppe.istDabei
    ? gruppe.ownerCount === 2
      ? 'Sie und eine weitere Anlage suchen'
      : `Sie und ${gruppe.ownerCount - 1} weitere Anlagen suchen`
    : gruppe.ownerCount === 1
    ? 'Eine Anlage sucht'
    : `${gruppe.ownerCount} Anlagen suchen`;

  // Zwei Arten, eine Menge einzustellen. Bei Range-Baellen die Staffel aus
  // der Feldkonfiguration, weil dort die Stufe ueber den Preis entscheidet.
  // Ueberall sonst eine freie Zahl - ein Ballautomat ist ein Ballautomat,
  // die Frage ist nur, wie viele.
  const staffel = gruppe.category === KATEGORIE_MIT_STAFFEL;
  // Wer schon dabei ist, hat seine Menge in der eigenen Anfrage stehen. Ein
  // zweites Feld hier wuerde sie doppelt zaehlen.
  // Drei Anlagen sind das Maximum. Ist es erreicht, gibt es hier nichts mehr
  // zu tun - dann ist die Kachel Auskunft und kein Angebot.
  const kannAnhaengen = !gruppe.istDabei && !gruppe.voll && gruppe.listings.length > 0;
  const zeigtMengen = kannAnhaengen && (staffel ? mengenOptionen.length > 0 : true);

  const eigene = menge ? parseInt(menge, 10) : 0;
  const hatEigene = Number.isFinite(eigene) && eigene > 0;
  const zusammen = hatEigene ? (gruppe.totalQuantity || 0) + eigene : gruppe.totalQuantity;

  // Die Gruppe kann zusammengehoeren, ohne dass jemand eine Zahl genannt
  // hat. Dann ist die Summe keine Summe, sondern nur die eigene Menge - das
  // muss dastehen, sonst liest sich eine 24 wie ein Buendel von 24.
  const ohneFremdmenge = gruppe.totalQuantity === null;

  const feldId = `fwb-menge-${String(gruppe.key).replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  // Die Menge der anderen gehoert in denselben Satz, nicht als zweite grosse
  // Zahl daneben. Zwei fette Zahlen auf einer Kachel streiten sich - und die
  // interessantere ist die untere: das, was mit der eigenen Menge
  // zusammenkaeme. Die obere ist nur die Ausgangslage.
  //
  // "zusammen" steht nur da, wo es wirklich eine Summe ist. Bei einer
  // einzigen Anfrage war es schlicht falsch.
  const summeIstSumme = gruppe.listings.length + (gruppe.eigene || []).length > 1;
  const fremdmenge =
    gruppe.totalQuantity !== null
      ? `${summeIstSumme ? 'zusammen ' : ''}${
          gruppe.partialQuantity ? 'mindestens ' : ''
        }${gruppe.totalQuantity.toLocaleString('de-DE')} Stück`
      : null;

  // Aufgeklappt stehen fremde und eigene Anfragen zusammen - so sieht man,
  // was das Buendel wirklich waere. Die eigene ist markiert.
  const eigeneIds = new Set((gruppe.eigene || []).map(l => l?.id?.uuid).filter(Boolean));
  const alleAnfragen = [...gruppe.listings, ...(gruppe.eigene || [])];

  const anhaengen = () => {
    // An die erste Anfrage der Gruppe anhaengen - sie bestimmt Kategorie,
    // Ausfuehrung und Frist. Alle in der Gruppe teilen diese Angaben ohnehin,
    // sonst waeren sie keine Gruppe.
    if (gruppe.listings.length === 0) return;
    const vorlage = baueVorlage(gruppe.listings[0], {
      menge: menge || null,
      // Was im Formular festliegt, im Klartext - hier gebaut, weil hier die
      // Beschriftungen aus der Feldkonfiguration schon vorliegen.
      beschreibung: merkmale ? `${label} · ${merkmale}` : label,
    });
    merkeVorlage(vorlage);
  };

  return (
    <article className={classNames(css.poolGroup, { [css.poolGroupOwn]: gruppe.istDabei })}>
      <h4 className={css.poolCategory}>
        {label}
        {/*
          Zwei Kacheln derselben Ausfuehrung nebeneinander sind sonst nicht
          auseinanderzuhalten - sie entstehen, weil mehr als drei Anlagen
          dasselbe suchen und daraus zwei Buendel werden.
        */}
        {gruppe.vonWievielen ? (
          <span className={css.poolNumber}>
            Bündel {gruppe.nummer} von {gruppe.vonWievielen}
          </span>
        ) : null}
        {gruppe.istDabei ? <span className={css.poolOwnBadge}>Ihre Anfrage</span> : null}
      </h4>
      {/*
        Was diese Gruppe bindet. Bei Range-Baellen Ausfuehrung und Farbe -
        18.000 zweistueckige gelbe sind eine Charge, dreimal 6.000
        verschiedene sind drei Bestellungen.
      */}
      {merkmale ? <p className={css.poolTraits}>{merkmale}</p> : null}

      <p className={css.poolFacts}>
        <span className={css.poolCount}>{anlagen}</span>
        {fremdmenge ? <span className={css.poolQty}>{fremdmenge}</span> : null}
      </p>

      {gruppe.countries.length > 0 ? (
        <p className={css.poolCountries}>
          Lieferung nach {gruppe.countries.join(', ').toUpperCase()}
        </p>
      ) : null}

      {zeigtMengen ? (
        <div className={css.poolCalc}>
          <label className={css.poolCalcLabel} htmlFor={feldId}>
            {staffel ? 'Ihre Menge' : 'Ihre Stückzahl'}
          </label>
          {staffel ? (
            <select
              id={feldId}
              className={css.poolSelect}
              value={menge}
              onChange={e => setMenge(e.target.value)}
            >
              <option value="">noch offen</option>
              {mengenOptionen.map(o => (
                <option key={o.option} value={o.option}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={feldId}
              className={css.poolSelect}
              type="number"
              inputMode="numeric"
              min="1"
              max="1000"
              step="1"
              placeholder="z. B. 24"
              value={menge}
              onChange={e => setMenge(e.target.value)}
            />
          )}
          {hatEigene ? (
            <p className={css.poolCalcResult}>
              {ohneFremdmenge ? (
                <>
                  Die anderen Anfragen nennen noch keine Stückzahl. Mit Ihren{' '}
                  <strong>{eigene.toLocaleString('de-DE')} Stück</strong> hätte das Bündel eine.
                </>
              ) : (
                <>
                  Zusammen wären das{' '}
                  <strong>
                    {gruppe.partialQuantity ? 'mindestens ' : ''}
                    {zusammen.toLocaleString('de-DE')} Stück
                  </strong>
                  .
                </>
              )}
            </p>
          ) : null}
        </div>
      ) : null}

      {/*
        Warum die eigene Anfrage hier ueberhaupt auftaucht: Sie gehoert zu
        diesem Buendel. Ein Buendel, in dem man schon drin ist, ist keine
        Gelegenheit mehr, sondern ein Stand der Dinge.
      */}
      {gruppe.istDabei ? (
        <p className={css.poolOwnNote}>
          Ihre Anfrage gehört zu diesem Bündel
          {gruppe.voll ? ', und es ist vollständig' : ''}. Anbieter sehen die Anfragen zusammen und
          können ein Sammelangebot rechnen; Ihre Anfrage bleibt Ihre.
        </p>
      ) : null}

      {/*
        Die Obergrenze sichtbar machen - in beide Richtungen. "Noch ein Platz
        frei" ist ein Grund, sich jetzt zu entscheiden; "voll" erspart die
        Suche nach dem Knopf, den es nicht gibt.
      */}
      {gruppe.voll && !gruppe.istDabei ? (
        <p className={css.poolFull}>
          Dieses Bündel ist vollständig – mehr als {MAX_ANLAGEN} Anlagen bestellen nicht gemeinsam.
        </p>
      ) : !gruppe.voll && !gruppe.istDabei && gruppe.listings.length > 0 ? (
        <p className={css.poolSeats}>
          {gruppe.freiePlaetze === 1
            ? 'Noch ein Platz frei'
            : `Noch ${gruppe.freiePlaetze} Plätze frei`}{' '}
          – ein Bündel umfasst höchstens {MAX_ANLAGEN} Anlagen.
        </p>
      ) : null}

      {/*
        Was am Buendel NICHT gemeinsam ist. Der Logodruck ist der Fall: Die
        Baelle kommen aus einer Charge, das Logo will jede Anlage selbst.
        Wer das nicht weiss, rechnet mit einem Preis, der so nicht gilt.
      */}
      {gruppe.hinweis ? <p className={css.poolNote}>{gruppe.hinweis}</p> : null}

      <button
        type="button"
        className={css.poolToggle}
        onClick={() => setOffen(o => !o)}
        aria-expanded={offen}
      >
        {offen
          ? 'Anfragen ausblenden'
          : `${alleAnfragen.length} ${alleAnfragen.length === 1 ? 'Anfrage' : 'Anfragen'} ansehen`}
      </button>

      {offen ? (
        <ul className={css.poolList}>
          {alleAnfragen.map(l => {
            const m = getQuantity(l);
            const frist = getDeadlineStatus(l);
            const eigen = eigeneIds.has(l?.id?.uuid);
            return (
              <li
                key={l?.id?.uuid}
                className={classNames(css.poolItem, { [css.poolItemOwn]: eigen })}
              >
                <span className={css.poolItemTitle}>
                  {l?.attributes?.title}
                  {eigen ? <span className={css.poolItemOwnTag}>Ihre</span> : null}
                </span>
                <span className={css.poolItemMeta}>
                  {m !== null ? `${m.toLocaleString('de-DE')} Stück` : 'ohne Mengenangabe'}
                  {frist ? ` · ${frist.label}` : ''}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/*
        Der Knopf steht nur da, wo er etwas tut. An die eigene Anfrage haengt
        man sich nicht an, und wer schon dabei ist, haengt sich nicht ein
        zweites Mal an - dann waeren es zwei Anfragen derselben Anlage.
      */}
      {kannAnhaengen ? (
        <NamedLink className={css.poolCta} name="NewListingPage" onClick={anhaengen}>
          Anfrage anhängen
        </NamedLink>
      ) : null}
    </article>
  );
};

/**
 * Der Bündel-Pool.
 *
 * Eine einzelne Anlage bestellt 6.000 Range-Bälle und zahlt Kleinmengenpreis.
 * Drei Anlagen, die im selben Zeitraum dasselbe suchen, bestellen zusammen
 * 18.000 – das ist eine andere Preisklasse. Diese Einkaufsmacht hat kein
 * einzelner Betrieb; sie entsteht nur, weil hier jemand die Nachfrage vieler
 * Anlagen gleichzeitig sieht.
 *
 * Der Pool zeigt deshalb bewusst alles, was offen ist – nicht nur das, was zu
 * den eigenen Anfragen passt. Wer erst filtert, was er ohnehin schon sucht,
 * findet nie etwas Neues.
 *
 * Zusammengeführt wird nichts: Jede Anlage bleibt Eigentümerin ihrer
 * Ausschreibung und entscheidet selbst über ihr Angebot. Wir zeigen nur, dass
 * da etwas zusammengehört.
 */
const BundlePool = ({ listings = [], eigeneAnfragen = [], currentUser }) => {
  const eigeneAnlageId = currentUser?.id?.uuid || null;
  // Zwei Wege, die eigene Anfrage zu erkennen - siehe poolGroups().
  const eigeneAnfrageIds = eigeneAnfragen.map(l => l?.id?.uuid).filter(Boolean);
  const gruppen = poolGroups(listings, { eigeneAnlageId, eigeneAnfrageIds });
  const mengenOptionen = useMengenOptionen();
  const merkmalText = useMerkmalText();

  // Eine Kachel ist eine Gelegenheit: Dort sucht mindestens eine ANDERE
  // Anlage dasselbe. Eine Gruppe, in der nur die eigene Anfrage steht, ist
  // keine Gelegenheit, sondern ein Zustand - und bekommt deshalb keine
  // Kachel. Sonst haette eine Anlage mit fuenf freigegebenen Anfragen fuenf
  // Kacheln, die nichts anbieten, und der Pool waere unlesbar.
  const chancen = gruppen.filter(g => g.listings.length > 0);

  // Der Zustand kommt aus den eigenen Anzeigen und nicht aus der Suche: So
  // steht er auch dann da, wenn die eigene Anfrage jenseits der 24 Treffer
  // liegt oder die Suche gerade fehlgeschlagen ist.
  const inKachel = new Set(
    chancen.flatMap(g => (g.eigene || []).map(l => l?.id?.uuid)).filter(Boolean)
  );
  // Welche der eigenen Anfragen wirklich im Pool stehen - aus ZWEI Quellen,
  // weil jede fuer sich unvollstaendig sein kann:
  //
  //   die eigene Anzeigenliste   ist auf eine Seite begrenzt
  //   die Poolsuche              liefert hoechstens 24 Treffer
  //
  // Fehlte eine Anfrage in der einen, stand sie bisher nirgends - sie war
  // im Pool und der Kasten schwieg. Vereinigt man beide, muss sie nur in
  // einer von beiden auftauchen.
  const imPool = new Map();
  eigeneAnfragen.forEach(l => {
    const id = l?.id?.uuid;
    if (id && stehtImPool(l)) imPool.set(id, l);
  });
  gruppen.forEach(g =>
    (g.eigene || []).forEach(l => {
      const id = l?.id?.uuid;
      // Aus der Suche, also zwangslaeufig veroeffentlicht und schon
      // geprueft - poolGroups laesst nichts anderes durch.
      if (id) imPool.set(id, l);
    })
  );

  const freigegeben = [...imPool.values()].filter(l => !inKachel.has(l?.id?.uuid));

  if (chancen.length === 0 && freigegeben.length === 0) {
    return null;
  }

  return (
    <section className={css.pool}>
      <h3 className={css.listingsHeading}>Bündel-Pool</h3>
      <p className={css.poolLead}>
        {chancen.length > 0
          ? 'Diese Anlagen haben ihre Anfrage für eine gemeinsame Bestellung geöffnet. Wer sich anhängt, schreibt dieselbe Menge zur selben Zeit aus – und die Anbieter rechnen ein Sammelangebot. Jede Anlage bleibt dabei Eigentümerin ihrer eigenen Anfrage.'
          : 'Hier stehen offene Anfragen anderer Anlagen, an die Sie sich anhängen können. Gerade sucht keine andere Anlage dasselbe wie Sie – das ändert sich, sobald eine neue Anfrage dazukommt.'}
      </p>

      {chancen.length > 0 ? (
        <div className={css.poolGroups}>
          {chancen.map(gruppe => (
            <PoolGroup
              key={gruppe.key}
              gruppe={gruppe}
              mengenOptionen={mengenOptionen}
              merkmalText={merkmalText}
            />
          ))}
        </div>
      ) : null}

      {/*
        Die Freigabe ist eine Einstellung, die man sonst nirgends wirken
        sieht. Ohne diesen Satz sieht es aus, als haette sie nichts getan -
        dabei ist die Anfrage laengst fuer andere Anlagen sichtbar.
      */}
      {freigegeben.length > 0 ? (
        <p className={css.poolOwnStatus}>
          <strong>
            {freigegeben.length === 1
              ? 'Ihre Anfrage steht im Pool.'
              : `Ihre ${freigegeben.length} Anfragen stehen im Pool.`}
          </strong>{' '}
          {freigegeben.map(l => l?.attributes?.title).join(' · ')} – freigegeben für Bündelung.
          Andere Anlagen sehen sie und können sich anhängen.
        </p>
      ) : null}
    </section>
  );
};

/**
 * Eine Anfrage in der Liste.
 *
 * Vorher stand unter jeder Anfrage sofort die ganze Vergleichstabelle. Bei
 * einer Anfrage geht das; bei fuenf ist die Startseite eine Wand, in der man
 * die eine sucht, um die es gerade geht.
 *
 * Jetzt ist die Kopfzeile die Anfrage - Bild, Titel, Zustand, Frist, Zahl der
 * Angebote - und der Rest klappt auf. Aufgeklappt startet nur, was eine
 * Entscheidung verlangt: Angebote liegen da und die Frist ist abgelaufen oder
 * laeuft bald ab. Alles andere kann warten und soll deshalb nicht im Weg
 * stehen.
 */
/**
 * Eine Anfrage, auf die nach Tagen nichts kommt.
 *
 * Bisher stand an so einer Anfrage nur "Noch keine Angebote" - richtig, aber
 * nutzlos. Die Frage dahinter lautet ja: Woran liegt es, und was kann ich
 * jetzt noch tun? Beides steht in der Anfrage selbst.
 *
 * Der Ton ist absichtlich kein Vorwurf. Eine Anfrage ohne Budgetrahmen ist
 * kein Fehler, sie kostet nur - und der Club erfaehrt das hier zu einem
 * Zeitpunkt, an dem Nachtragen noch wirkt. Steht alles drin und es kommt
 * trotzdem nichts, sagen wir genau das, statt eine Aufgabe zu erfinden.
 */
const StilleAnfrage = ({ still, listing }) => {
  const id = listing?.id?.uuid;
  const titel = listing?.attributes?.title || '';

  return (
    <div className={css.still}>
      <p className={css.stillKopf}>
        Seit {still.tage} Tagen offen, noch kein Angebot
        {still.restTage !== null ? ` · noch ${still.restTage} Tage Frist` : ''}
      </p>

      {still.nichtsZuTun ? (
        <p className={css.stillText}>
          An Ihrer Anfrage liegt es nicht – sie nennt Menge, Budget, Termin und Leistungen. In
          manchen Kategorien dauert es schlicht: Anbieter sammeln Anfragen und rechnen sie am Stück.
          Bleibt es dabei, hilft meist eine längere Frist.
        </p>
      ) : (
        <>
          <p className={css.stillText}>Das kann an der Anfrage liegen. Was jetzt noch wirkt:</p>
          <ul className={css.stillListe}>
            {still.vorschlaege.map(v => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </>
      )}

      {id ? (
        <NamedLink
          className={css.stillLink}
          name="EditListingPage"
          params={{ id, slug: createSlug(titel), type: 'edit', tab: 'details' }}
        >
          Anfrage bearbeiten
        </NamedLink>
      ) : null}
    </div>
  );
};

const Anfrage = ({ listing, offers, relatedListings, currentUser, offen: offenVorgabe }) => {
  const id = listing?.id?.uuid;
  const state = listing?.attributes?.state;
  const deadline = getDeadlineStatus(listing);
  const summary = summarizeOffers(offers);
  const [offen, setOffen] = useState(offenVorgabe);
  // Dieselbe Bedingung wie im Pool-Kasten. Vorher fehlten hier der
  // Anzeigenzustand und die Eindeutigkeit - ein Entwurf trug das Etikett
  // "Im Bündel-Pool", ohne dort zu stehen.
  const imPool = stehtImPool(listing);

  const still = stilleAnfrage(listing, offers);

  const angebote =
    summary.open === 0
      ? 'Noch keine Angebote'
      : summary.bestLabel
      ? `${summary.open} ${summary.open === 1 ? 'Angebot' : 'Angebote'} · ab ${summary.bestLabel}`
      : `${summary.open} ${summary.open === 1 ? 'Angebot' : 'Angebote'}`;

  return (
    <article className={classNames(css.request, { [css.requestOpen]: offen })}>
      <button
        type="button"
        className={css.requestHead}
        onClick={() => setOffen(o => !o)}
        aria-expanded={offen}
      >
        <img className={css.requestImage} src={bildFuer(listing)} alt="" loading="lazy" />

        <span className={css.requestMain}>
          <span className={css.requestTitle}>{listing?.attributes?.title}</span>
          <span className={css.requestBadges}>
            <span
              className={classNames(css.listingState, {
                [css.statePublished]: state === 'published',
              })}
            >
              {STATE_LABELS[state] || state}
            </span>
            {deadline ? (
              <span
                className={classNames(css.deadline, {
                  [css.deadlineUrgent]: deadline.urgent,
                  [css.deadlineExpired]: deadline.expired,
                })}
                title={`Angebotsfrist bis ${deadline.dateLabel}`}
              >
                {deadline.label}
              </span>
            ) : null}
            <span
              className={classNames(css.requestOffers, {
                [css.requestOffersSome]: summary.open > 0,
                // Schweigen nach ein paar Tagen ist kein Zustand mehr,
                // sondern eine Nachricht.
                [css.requestOffersStill]: !!still,
              })}
            >
              {angebote}
            </span>
            {/*
              Die Buendelfreigabe steht hier, weil man hier auf seine Anfrage
              schaut. Im Pool ist sie eine Randnotiz, hier ist sie der
              Zustand dieser einen Anfrage.
            */}
            {imPool ? <span className={css.requestPool}>Im Bündel-Pool</span> : null}
          </span>
        </span>

        <span className={css.requestChevron} aria-hidden="true" />
      </button>

      {offen ? (
        <div className={css.requestBody}>
          {still ? <StilleAnfrage still={still} listing={listing} /> : null}

          <BundleHint listing={listing} candidates={relatedListings} currentUser={currentUser} />
          <OfferComparison
            offers={offers}
            best={summary.best}
            listing={listing}
            clubName={currentUser?.attributes?.profile?.displayName || ''}
          />
          {offers.length > 0 ? (
            <AwardRecordButton listing={listing} offers={offers} currentUser={currentUser} />
          ) : null}
          <NamedLink
            className={css.requestLink}
            name="ListingPage"
            params={{ id, slug: 'anfrage' }}
          >
            Anfrage öffnen
          </NamedLink>
        </div>
      ) : null}
    </article>
  );
};

/**
 * Die Liste der eigenen Anfragen.
 *
 * "Alle aufklappen" steht nur da, wo es etwas zu tun gibt - bei einer
 * einzelnen Anfrage waere es ein Knopf fuer nichts.
 */
const AnfrageListe = ({ listings, offersByListing, relatedListings, currentUser }) => {
  const wichtig = l => {
    const d = getDeadlineStatus(l);
    const s = summarizeOffers(offersByListing[(l?.id?.uuid)] || []);
    return s.open > 0 && (!d || d.urgent || d.expired);
  };
  /*
    Drei Zustaende, nicht zwei: null heisst "noch nicht angefasst" - dann
    entscheidet jede Anfrage selbst, ob sie offen startet. Erst wenn jemand
    den Knopf drueckt, gilt seine Entscheidung fuer alle. Mit einem einfachen
    true/false waere "Alle zuklappen" wirkungslos, weil die wichtigen
    Anfragen sofort wieder aufgingen.
  */
  const [alle, setAlle] = useState(null);

  return (
    <>
      <div className={css.requestsHead}>
        <h3 className={css.listingsHeading}>Ihre Anfragen</h3>
        {listings.length > 1 ? (
          <button type="button" className={css.toggleAll} onClick={() => setAlle(a => a !== true)}>
            {alle === true ? 'Alle zuklappen' : 'Alle aufklappen'}
          </button>
        ) : null}
      </div>

      <div className={css.requests}>
        {listings.map(l => (
          // Der Schluessel enthaelt den Zustand von "Alle aufklappen": React
          // baut die Karte damit neu auf und uebernimmt die Vorgabe. Ohne das
          // wuerde der eigene Zustand jeder Karte den Knopf ignorieren.
          <Anfrage
            key={`${l?.id?.uuid}-${alle}`}
            listing={l}
            offers={offersByListing[(l?.id?.uuid)] || []}
            relatedListings={relatedListings}
            currentUser={currentUser}
            offen={alle === null ? listings.length === 1 || wichtig(l) : alle}
          />
        ))}
      </div>
    </>
  );
};

const OwnListings = ({ listings, audience, currentUser, offers = [], relatedListings = [] }) => {
  if (audience === 'provider') {
    const used = countOffersInMonth(offers);
    const check = canSubmitOffer(currentUser, used);
    const allowance = check.allowance || getOfferAllowance(currentUser);

    const text = allowance.unlimited
      ? allowance.founding
        ? `Als Gründungsmitglied geben Sie unbegrenzt viele Angebote ab. Diesen Monat waren es bisher ${used}.`
        : `Ihr Paket ${allowance.plan.label} erlaubt unbegrenzt viele Angebote. Diesen Monat waren es bisher ${used}.`
      : check.allowed
      ? `Paket ${allowance.plan.label}: ${allowance.limit} Angebote pro Monat, davon sind diesen Monat noch ${check.remaining} frei.`
      : `Paket ${allowance.plan.label}: Ihre ${allowance.limit} Angebote für diesen Monat sind aufgebraucht. Ab dem 1. des nächsten Monats geht es weiter – oder Sie wechseln in ein größeres Paket.`;

    return (
      <>
        <h3 className={css.listingsHeading}>Ihr Angebotskontingent</h3>
        <p className={classNames(css.empty, { [css.quotaSpent]: !check.allowed })}>
          {text} <NamedLink name="MembershipPage">Tarif ansehen</NamedLink>
        </p>
        <MatchingListings
          listings={relatedListings}
          currentUser={currentUser}
          eigeneAngebote={offers}
        />
      </>
    );
  }

  const offersByListing = groupOffersByListing(offers);
  const reorder = getReorderSuggestions(listings);

  return (
    <>
      {reorder.length > 0 ? (
        <div className={css.reorder}>
          <h3 className={css.listingsHeading}>Wiederbeschaffung</h3>
          {reorder.map(entry => (
            <p key={entry.listing?.id?.uuid} className={css.reorderText}>
              Ihre letzte Anfrage zu{' '}
              <strong>{CATEGORY_LABELS[entry.category] || 'dieser Kategorie'}</strong> liegt{' '}
              {entry.months} Monate zurück. Erfahrungswert für den Range-Betrieb: {entry.due}{' '}
              Monate.{' '}
              {istVorStart() ? null : (
                <NamedLink name="NewListingPage">Neue Anfrage einstellen</NamedLink>
              )}
            </p>
          ))}
        </div>
      ) : null}

      <BundlePool listings={relatedListings} eigeneAnfragen={listings} currentUser={currentUser} />

      {listings.length === 0 ? (
        <>
          <h3 className={css.listingsHeading}>Ihre Anfragen</h3>
          <p className={css.empty}>
            Sie haben noch keine Anfrage eingestellt. Beschreiben Sie einmal, was Sie brauchen – die
            passenden Anbieter melden sich bei Ihnen.
          </p>
        </>
      ) : (
        <AnfrageListe
          listings={listings}
          offersByListing={offersByListing}
          relatedListings={relatedListings}
          currentUser={currentUser}
        />
      )}
    </>
  );
};

/**
 * Vergabevermerk zum Ausdrucken.
 *
 * Vereine und kommunale Anlagen muessen begruenden, warum ein Anbieter den
 * Zuschlag bekommen hat. Wir oeffnen ein fertiges Dokument in einem neuen
 * Fenster; drucken oder als PDF sichern macht der Browser. Bewusst ohne
 * zusaetzliche Bibliothek - ein Vermerk, der an einem Paket haengt, ist in
 * zwei Jahren kaputt.
 */
const AwardRecordButton = ({ listing, offers, currentUser }) => {
  const open = () => {
    const html = buildAwardHtml({
      listing,
      offers,
      clubName: currentUser?.attributes?.profile?.displayName || '',
    });
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  return (
    <button type="button" className={css.awardButton} onClick={open}>
      Vergabevermerk erstellen
    </button>
  );
};

/**
 * Buendelhinweis an einer eigenen Anfrage.
 *
 * Zeigt der Anlage, dass andere im selben Zeitraum dasselbe suchen und fuer
 * eine Buendelbestellung offen sind. Bewusst ohne Namen: Wer mitmacht, geht
 * die anderen Anlagen nichts an, solange kein Angebot vorliegt. Die Zahl
 * allein reicht, um zu verstehen, dass hier Menge zusammenkommt.
 */
const BundleHint = ({ listing, candidates, currentUser }) => {
  const feldInfo = useFeldInfo();
  const offen = isStillOpen(listing);
  const freigegeben = isBundleOpen(listing);

  // Eine abgelaufene Anfrage buendelt niemand mehr - dazu gibt es nichts
  // mehr zu sagen.
  if (!offen) return null;

  // Nicht freigegeben: einmal sagen, was die Freigabe koennte. Leise, weil
  // es eine Entscheidung der Anlage ist und keine Aufforderung.
  if (!freigegeben) {
    return (
      <p className={css.bundleClosed}>
        Diese Anfrage ist nicht für Bündelung freigegeben. Mit Freigabe erscheint sie im
        Bündel-Pool, andere Anlagen können sich anhängen – und aus mehreren kleinen Mengen wird
        eine, die Anbieter anders kalkulieren.
      </p>
    );
  }

  // Freigegeben, aber ein bindendes Merkmal fehlt. Das trifft vor allem
  // Anfragen aus der Zeit, als es diese Felder noch nicht gab: Sie bleiben
  // stehen, wie sie sind, nur buendeln koennen sie nicht, solange niemand
  // weiss, wovon neun Stueck.
  const fehlt = fehlendeFelder(listing);
  if (fehlt.length > 0) {
    const namen = fehlt.map(key => feldInfo(key, null).label).join(', ');

    return (
      <p className={css.bundleClosed}>
        <strong>Für ein Bündel fehlt noch eine Angabe.</strong> Eine gemeinsame Bestellung kommt aus
        einer Lieferung – dafür muss feststehen, wovon: {namen}. Ergänzen Sie das in Ihrer Anfrage,
        dann steht sie im Pool. Für die Ausschreibung allein ändert sich nichts.
      </p>
    );
  }

  // Freigegeben, aber mehrdeutig: Eine gemeinsame Bestellung kommt aus einer
  // Charge, und eine Charge hat eine Farbe. Das ist kein Fehler der Anfrage -
  // nur eine Anfrage, die so nicht buendelbar ist.
  const unklar = mehrdeutigeFelder(listing);
  if (unklar.length > 0) {
    const pd = listing?.attributes?.publicData || {};
    const offen = unklar
      .map(key => {
        const { label, lesbar } = feldInfo(key, pd[key]);
        return `${label}: ${lesbar}`;
      })
      .join(' · ');

    return (
      <p className={css.bundleClosed}>
        <strong>Für ein Bündel muss es eindeutig sein.</strong> Eine gemeinsame Bestellung kommt aus
        einer Charge – je Merkmal kann darin nur ein Wert stehen. Ihre Anfrage lässt offen: {offen}.
        Für die Ausschreibung allein ist das völlig in Ordnung und oft sogar klug; für den
        Bündel-Pool müssten Sie sich festlegen.
      </p>
    );
  }

  // Die eigene Anzeige kommt ohne Autorenangabe hier an - ohne die eigene
  // Anlagen-ID zaehlte eine zweite eigene Anfrage derselben Art als "weitere
  // Anlage", und man buendelte mit sich selbst.
  const partners = findBundlePartners(listing, candidates, {
    eigeneAnlageId: currentUser?.id?.uuid || null,
  });

  // Freigegeben, aber noch allein. Das ist kein Nichts: Die Anfrage steht im
  // Pool und ist fuer andere Anlagen sichtbar.
  if (partners.length === 0) {
    return (
      <p className={css.bundleClosed}>
        <strong>Für Bündelung freigegeben.</strong> Ihre Anfrage steht im Bündel-Pool und ist für
        andere Anlagen sichtbar. Bisher sucht keine dasselbe – sobald sich das ändert, steht es
        hier.
      </p>
    );
  }

  const { totalQuantity, partialQuantity } = summarizeBundle(listing, partners);

  // Die eigene Anfrage kommt hier ohne Autorenangabe an - ihr Eigentuemer
  // faellt in summarizeBundle also aus der Zaehlung heraus. Fuer den Satz
  // zaehlen ohnehin nur die anderen Anlagen, und die stehen in partners.
  const weitere = new Set(partners.map(getAuthorId).filter(Boolean)).size || partners.length;
  const menge = totalQuantity
    ? `${partialQuantity ? 'mindestens ' : ''}${totalQuantity.toLocaleString('de-DE')}`
    : null;

  return (
    <p className={css.bundle}>
      <strong>Bündelbestellung möglich.</strong>{' '}
      {weitere === 1
        ? 'Eine weitere Anlage sucht im selben Zeitraum dasselbe und ist für eine Bündelbestellung offen.'
        : `${weitere} weitere Anlagen suchen im selben Zeitraum dasselbe und sind für eine Bündelbestellung offen.`}{' '}
      {menge
        ? `Zusammen sind das ${menge} Stück – eine andere Preisklasse als Ihre Menge allein.`
        : 'Anbieter sehen das und können ein Sammelangebot rechnen.'}
    </p>
  );
};

/**
 * Passende Anfragen: offene Ausschreibungen aus den Kategorien und
 * Liefergebieten, die der Haendler in seinem Profil gepflegt hat. Dringendes
 * steht oben, weil ein Haendler seinen Tag danach einteilt.
 *
 * Bewusst unabhaengig vom Tarif: Das Kontingent begrenzt, wie viele Angebote
 * abgegeben werden, nicht was man sieht. Wer acht passende Anfragen sieht und
 * nur zwei beantworten darf, hat den besten Grund fuer ein groesseres Paket –
 * wer nichts sieht, hat keinen Grund wiederzukommen.
 */
const MatchingListings = ({ listings, currentUser, eigeneAngebote = [] }) => {
  const hasProfile = getDealerCategories(currentUser).length > 0;
  const isApproved = isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser);

  // Solange die Anmeldung noch geprueft wird, darf das Konto keine Anfragen
  // sehen. Das ehrlich zu sagen ist besser als "keine Anfrage offen" – sonst
  // sucht der Haendler den Fehler bei sich.
  if (!isApproved) {
    return (
      <>
        <h3 className={css.listingsHeading}>Passende Anfragen</h3>
        <p className={css.empty}>
          Ihre Anmeldung wird gerade geprüft. Sobald Ihr Konto freigeschaltet ist, sehen Sie hier
          die offenen Anfragen aus Ihren Kategorien.
        </p>
      </>
    );
  }

  if (!hasProfile) {
    return (
      <>
        <h3 className={css.listingsHeading}>Passende Anfragen</h3>
        <p className={css.empty}>
          Sobald Sie in Ihrem Profil hinterlegen, welche Produktkategorien Sie liefern und in welche
          Länder, zeigen wir Ihnen hier die passenden offenen Anfragen.{' '}
          <NamedLink name="ProfileSettingsPage">Profil ergänzen</NamedLink>
        </p>
      </>
    );
  }

  const feldInfo = useFeldInfo();

  const open = sortByUrgency(listings).filter(l => {
    const status = getDeadlineStatus(l);
    return !status || !status.expired;
  });

  // Zeitvorsprung des Tarifs: Was noch nicht freigegeben ist, bleibt sichtbar,
  // aber ohne Titel. Der Haendler weiss, dass da etwas ist – das ist der
  // ehrlichste Moment, ueber ein groesseres Paket zu sprechen.
  const released = open.filter(l => isReleasedForDealer(l, currentUser));
  const waiting = open.filter(l => !isReleasedForDealer(l, currentUser));
  const delayHours = getVisibilityDelayHours(currentUser);

  // Mehrere Anlagen suchen dasselbe und sind fuer ein Buendel offen: Fuer den
  // Haendler ist das eine Position statt drei - eine Kalkulation, eine
  // Lieferung, drei Abschluesse.
  const bundles = groupBundles(released);

  /*
    Uebersicht statt Stapel: Die Buendelhinweise standen vorher alle
    untereinander am Kopf der Liste - zwei fast gleich aussehende gruene
    Kaesten, und erst beim dritten Lesen merkt man, dass sie zu
    unterschiedlichen Anfragen gehoeren. Jetzt steht jeder Hinweis ueber
    genau den Kacheln, um die es geht, und alles Uebrige folgt darunter.
  */
  const imBuendel = new Set();
  bundles.forEach(g => g.listings.forEach(l => l?.id?.uuid && imBuendel.add(l.id.uuid)));
  const einzeln = released.filter(l => !imBuendel.has(l?.id?.uuid));

  // Die Dealchance gehoert auf jede Kachel: Ein Haendler, der acht Anfragen
  // sieht, entscheidet nicht nach Reihenfolge, sondern danach, wo er wirklich
  // eine Chance hat. Ohne Profil gibt es keine Zahl - dann steht dort nichts.
  /*
    Woran der Haendler sonst nicht denkt: Er hat hier schon geboten. Ohne
    Markierung rechnet er dieselbe Anfrage ein zweites Mal durch, bis er im
    Posteingang merkt, dass sein Angebot laengst draussen ist. Die Kachel
    bleibt stehen - sie verschwinden zu lassen waere schlimmer, dann sucht
    er sie.
  */
  const angeboteJeAnfrage = groupOffersByListing(eigeneAngebote);

  const Kachel = ({ listing: l }) => {
    const id = l?.id?.uuid;
    const chance = dealChance(currentUser, l);
    const eigene = angeboteJeAnfrage[id] || [];
    const abgegeben = eigene.length > 0;

    return (
      <article className={classNames(css.anfrageKachel, { [css.kachelAbgegeben]: abgegeben })}>
        <div className={css.kachelKopf}>
          <div className={css.kachelText}>
            <span className={css.listingEyebrow}>{getCategoryLabel(l)}</span>
            <NamedLink
              className={css.listingTitle}
              name="ListingPage"
              params={{ id, slug: 'anfrage' }}
            >
              {l?.attributes?.title}
            </NamedLink>
          </div>

          {chance ? (
            <div className={css.kachelChance}>
              <DealRing wert={chance.wert} stufe={chance.stufe.name} klein />
              <span className={css.chanceText}>{chance.stufe.text}</span>
            </div>
          ) : null}
        </div>

        {/*
          Frist, Menge, Budget, Lieferland, Termin - derselbe Baustein,
          den auch die Suchergebnisse benutzen. Was dort geaendert wird,
          aendert sich hier mit.
        */}
        <AnfrageChips listing={l} className={css.listingChips} />

        {abgegeben ? (
          <p className={css.abgegeben}>
            <span className={css.abgegebenHaken} aria-hidden="true">
              ✓
            </span>
            {eigene.length === 1 ? 'Angebot abgegeben' : `${eigene.length} Angebote abgegeben`} · im{' '}
            <NamedLink name="InboxPage" params={{ tab: 'sales' }} className={css.abgegebenLink}>
              Posteingang
            </NamedLink>
          </p>
        ) : null}
      </article>
    );
  };

  return (
    <>
      <h3 className={css.listingsHeading}>Passende Anfragen</h3>

      {open.length === 0 ? (
        <p className={css.empty}>
          In Ihren Kategorien ist gerade keine Anfrage offen. Sobald eine Golfanlage etwas
          ausschreibt, was Sie liefern, steht sie hier.
        </p>
      ) : null}

      {bundles.map(group => {
        const quantities = group.listings.map(getQuantity).filter(q => q !== null);
        const total = quantities.length > 0 ? quantities.reduce((a, b) => a + b, 0) : null;
        const land = COUNTRY_LABELS[group.country] || group.country;

        return (
          <section className={css.buendelBlock} key={`${group.category}|${group.country}`}>
            <header className={css.buendelKopf}>
              <span className={css.buendelMarke}>Sammelangebot</span>
              <span className={css.buendelZeile}>
                {group.ownerCount} Anlagen ·{' '}
                {CATEGORY_LABELS[group.category] || 'dieselbe Kategorie'}
                {land ? ` · Lieferung nach ${land}` : ''}
                {total ? ` · zusammen ${total.toLocaleString('de-DE')} Stück` : ''}
              </span>
              <span className={css.buendelZusatz}>
                Eine Kalkulation, eine Lieferung, {group.ownerCount} Abschlüsse.
              </span>
            </header>

            <div className={css.anfrageGrid}>
              {group.listings.map(l => (
                <Kachel key={l?.id?.uuid} listing={l} />
              ))}
            </div>
          </section>
        );
      })}

      {einzeln.length > 0 || waiting.length > 0 ? (
        <div className={css.anfrageGrid}>
          {einzeln.map(l => (
            <Kachel key={l?.id?.uuid} listing={l} />
          ))}

          {/*
            Die noch nicht freigegebenen: dieselbe Kachel, nur ohne Titel und
            ohne Zahlen. Sie hier mitzuzeigen ist ehrlicher, als sie zu
            verschweigen - der Haendler weiss, dass da etwas ist, und das ist
            der richtige Moment, ueber ein groesseres Paket zu sprechen.
          */}
          {waiting.map(l => {
            const id = l?.id?.uuid;
            const wait = formatWaitingTime(getReleaseTime(l, currentUser));
            return (
              <article key={id} className={classNames(css.anfrageKachel, css.listingLocked)}>
                <span className={css.listingEyebrow}>{getCategoryLabel(l)}</span>
                <span className={css.listingTitle}>Neue Anfrage</span>
                <span className={css.listingChips}>
                  <span className={css.chip}>
                    {wait ? `für Sie freigegeben ${wait}` : 'wird in Kürze freigegeben'}
                  </span>
                </span>
              </article>
            );
          })}
        </div>
      ) : null}

      {waiting.length > 0 ? (
        <p className={css.upsell}>
          {waiting.length === 1
            ? 'Eine passende Anfrage ist'
            : `${waiting.length} passende Anfragen sind`}{' '}
          für Premium-Mitglieder schon offen. Ihr Paket sieht neue Anfragen {delayHours} Stunden
          später – wer früher antwortet, ist im Vergleich der Anlage weiter vorn.
        </p>
      ) : null}
    </>
  );
};

/**
 * Angebotsvergleich: alle Angebote zu einer Anfrage nebeneinander, guenstigstes
 * zuerst. Bezahlte Tarife bekommen ein Abzeichen, aber keinen besseren Platz –
 * ein Preisvergleich, der die Reihenfolge verkauft, waere fuer Clubs wertlos.
 */
const OfferComparison = ({ offers, best, listing, clubName }) => {
  const sorted = sortOffersForComparison(offers);
  if (sorted.length === 0) return null;

  const bestId = best?.id?.uuid;

  // Eine Zeile je Kriterium, eine Spalte je Anbieter – so liest ein Club den
  // Vergleich in einem Blick, statt sieben Angebote nacheinander zu oeffnen.
  return (
    <div className={css.offerTableScroll}>
      <table className={css.offerTable}>
        <thead>
          <tr>
            <th className={css.offerTableCriterion}>Angebote</th>
            {sorted.map(tx => {
              const provider = tx?.provider;
              const plan = getPlan(provider);
              const isBest = tx?.id?.uuid === bestId;
              return (
                <th
                  key={tx?.id?.uuid}
                  className={classNames(css.offerTableHead, { [css.offerBest]: isBest })}
                >
                  {provider?.attributes?.profile?.displayName || 'Anbieter'}
                  {plan.badge ? <span className={css.offerPlan}>{plan.label}</span> : null}
                  {isBest ? <span className={css.offerBestTag}>Günstigster Preis</span> : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th className={css.offerTableCriterion}>Gesamtpreis netto</th>
            {sorted.map(tx => (
              <td
                key={tx?.id?.uuid}
                className={classNames(css.offerTableCell, css.offerAmount, {
                  [css.offerBest]: tx?.id?.uuid === bestId,
                })}
              >
                {formatAmount(getOfferAmount(tx)) || 'auf Anfrage'}
              </td>
            ))}
          </tr>
          {OFFER_FIELDS.map(field => (
            <tr key={field.key}>
              <th className={css.offerTableCriterion}>{field.label}</th>
              {sorted.map(tx => {
                const raw = getOfferFieldRaw(tx, field.key);
                const value = getOfferFieldValue(field.key, raw);
                return (
                  <td
                    key={tx?.id?.uuid}
                    className={classNames(css.offerTableCell, {
                      [css.offerBest]: tx?.id?.uuid === bestId,
                      [css.offerTableMissing]: !value,
                    })}
                  >
                    {value || 'keine Angabe'}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr>
            <th className={css.offerTableCriterion}>Liefergebiet und Service</th>
            {sorted.map(tx => (
              <td
                key={tx?.id?.uuid}
                className={classNames(css.offerTableCell, {
                  [css.offerBest]: tx?.id?.uuid === bestId,
                })}
              >
                {describeCoverage(tx?.provider, listing?.attributes?.publicData?.delivery_country)}
              </td>
            ))}
          </tr>
          <tr>
            <th className={css.offerTableCriterion}>Status</th>
            {sorted.map(tx => (
              <td
                key={tx?.id?.uuid}
                className={classNames(css.offerTableCell, {
                  [css.offerBest]: tx?.id?.uuid === bestId,
                })}
              >
                {STATUS_LABELS[getOfferStatus(tx)] || getOfferStatus(tx)}
              </td>
            ))}
          </tr>
          <tr>
            <th className={css.offerTableCriterion}>Einzelnes Angebot</th>
            {sorted.map(tx => (
              <td
                key={tx?.id?.uuid}
                className={classNames(css.offerTableCell, {
                  [css.offerBest]: tx?.id?.uuid === bestId,
                })}
              >
                <OfferPdfButton
                  offer={tx}
                  listing={listing}
                  clubName={clubName}
                  total={sorted.length}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/**
 * Der Hinweis vor dem Start.
 *
 * Wer sich vor dem 1. Januar anmeldet, kommt auf ein Dashboard ohne
 * Anfragen und ohne Angebote - und muesste raten, ob das an ihm liegt, an
 * einem Fehler oder daran, dass es noch nicht losgeht. Also steht es da.
 *
 * Mit der Zahl der Tage, weil "demnaechst" kein Termin ist, und mit dem
 * einen Handgriff, der jetzt schon etwas bringt: das Profil fuellen. Danach
 * richtet sich beim Haendler, welche Anfragen ihn ab dem ersten Tag
 * erreichen.
 */
const Vorstart = ({ audience }) => {
  const texte = VORSTART_TEXTE[audience] || VORSTART_TEXTE.customer;
  const tage = tageBisStart();

  return (
    <section className={css.vorstart}>
      <div className={css.vorstartZaehler}>
        <span className={css.vorstartZahl}>{tage}</span>
        <span className={css.vorstartEinheit}>{tage === 1 ? 'Tag' : 'Tage'}</span>
        <span className={css.vorstartDatum}>bis {LAUNCH_DATE_LABEL}</span>
      </div>

      <div className={css.vorstartText}>
        <h2 className={css.vorstartTitel}>{texte.titel}</h2>
        <p className={css.vorstartAbsatz}>{texte.text}</p>
        <p className={css.vorstartHinweis}>{texte.hinweis}</p>
        <NamedLink name="ProfileSettingsPage" className={css.vorstartLink}>
          Profil vervollständigen
        </NamedLink>
      </div>
    </section>
  );
};

const SectionDashboard = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    options,
    audience = 'customer',
    ownListings = [],
    offers = [],
    relatedListings = [],
    currentUser,
  } = props;

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  // Die Ueberschrift "Willkommen zurueck" samt Erklaerung sagt genau das,
  // was die Kacheln darunter jetzt zeigen. Ausgeblendet wird nur sie - der
  // Abschnitt bleibt, denn er IST das Dashboard.
  const kopfDoppelt = istDoppelteUeberschrift(title);
  const hasHeaderFields = hasDataInFields([title, description], fieldOptions) && !kopfDoppelt;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
        </header>
      ) : null}
      <div className={css.dashboardMain}>
        {/*
          Vor dem Start steht der Hinweis ganz oben - vor Wetter und Periode.
          Was heute nicht geht, ist die erste Auskunft, die jemand braucht.
        */}
        {istVorStart() ? <Vorstart audience={audience} /> : null}

        {/*
          Der Empfang: Tag, Wetter und der eine Weg, der von hier aus zaehlt.
          Er steht vor der Periode, weil er die Frage "was ist heute"
          beantwortet und die Periode die Frage "was ist dieses Jahr".
        */}
        <SectionWelcome
          audience={audience}
          currentUser={currentUser}
          offeneAnfragen={
            // Die Zahl fuer die Statuszeile des Leitstands: offene Anfragen
            // aus den Kategorien des Haendlers. Solange die Suche laeuft,
            // ist die Liste leer - dann lieber "wird abgefragt" als eine
            // Null, die gleich wieder springt.
            relatedListings.length === 0
              ? null
              : relatedListings.filter(l => !getDeadlineStatus(l)?.expired).length
          }
        />
        <SeasonBanner audience={audience} currentUser={currentUser} />
        <OwnListings
          listings={ownListings}
          audience={audience}
          currentUser={currentUser}
          offers={offers}
          relatedListings={relatedListings}
        />
      </div>
    </SectionContainer>
  );
};

export default SectionDashboard;
